import {
  ArrowUpRight,
  DollarSign,
  Lightbulb,
  Mail,
  MessageCircle,
  type LucideIcon,
} from 'lucide-react-native';
import { useCallback } from 'react';
import { Linking, Pressable, StyleSheet, View } from 'react-native';

import { Card } from '@/components/Card';
import { FaqCard } from '@/components/FaqCard';
import { StepNumber } from '@/components/StepNumber';
import { Text } from '@/components/Text';
import { colors, fontSizes, radii, spacing } from '@/constants/theme';
import type { Deal } from '@/types/deal';

/**
 * Renders the Refer-tab footer from `deal.marketing_copy`. The server is the
 * only source of copy (see `Deals::MarketingCopySchema`) — a section the
 * server didn't send is omitted, not filled in.
 *
 * This used to fall back to hardcoded SoFi Checking copy: its friend-steps,
 * its six-question FAQ, its terms. That was survivable while Checking was the
 * only referable deal, because the fallback WAS that deal's copy. It stops
 * being survivable the moment a second deal renders this component — SoFi
 * Invest has no authored `faq`, so it would have shown Checking's, telling
 * ambassadors their friend opens a "SoFi Checking & Savings account" for $50
 * and quoting a $20 referrer cut that isn't Invest's. Wrong terms and wrong
 * dollar amounts read as authoritative. An absent card just reads as absent.
 */

const CONTENT_IDEAS_URL = 'https://app.archive.com/s/f/18edefed-ca3f-43db-9794-11ef8e83ad98';
const DISCORD_URL = 'https://discord.gg/D8ws3ggWDp';

interface HowItWorksFooterProps {
  /** Hero deal (opt-in not required since 2026-08-19). Drives display_name + per-referral $$ + copy. */
  deal: Deal | null;
  /** Pre-computed referrer cut in whole dollars, derived from deal upstream. */
  perReferralDollars: number;
  /**
   * Invoked when the user taps the "Still have questions?" row. Parent
   * owns the mail-open + clipboard-fallback logic so the toast/error
   * surfaces stay co-located with the Refer tab's other UX feedback.
   */
  onContactSupport: () => void;
}

export function HowItWorksFooter({ deal, perReferralDollars, onContactSupport }: HowItWorksFooterProps) {
  const offerName = deal?.display_name ?? 'this brand';
  const copy = deal?.marketing_copy;

  const rewards: { icon: LucideIcon; title: string; body: string }[] = [
    {
      icon: DollarSign,
      title: `$${perReferralDollars} cash per referral`,
      body: `Hits your MarketPryce balance every time a friend signs up to ${offerName} through your link.`,
    },
  ];

  // Each of these is null when the server sent nothing, and its card is
  // skipped below. Never substitute another deal's copy.
  const friendSteps = copy?.friend_steps?.length ? copy.friend_steps : null;

  const faqs = copy?.faq?.length ? copy.faq : null;

  const terms = copy?.compliance_disclosure?.trim() || null;

  const handleOpenUrl = useCallback(async (url: string) => {
    try {
      await Linking.openURL(url);
    } catch {
      // Linking rejects on unhandled schemes; nothing reasonable to do
      // here besides swallow. The links are static https — failure is
      // a device-config edge case, not a user-actionable error.
    }
  }, []);

  return (
    <View style={styles.container}>
      <Card variant="flat" padding={0} style={styles.card}>
        <CardTitle>Your reward</CardTitle>
        <View style={styles.itemList}>
          {rewards.map((reward) => (
            <RewardRow key={reward.title} {...reward} />
          ))}
        </View>
      </Card>

      {friendSteps ? (
        <Card variant="flat" padding={0} style={styles.card}>
          <CardTitle>What your friend has to do</CardTitle>
          <View style={styles.itemList}>
            {friendSteps.map((body, i) => (
              <StepRow key={i} number={i + 1} body={body} />
            ))}
          </View>
        </Card>
      ) : null}

      <Card variant="flat" padding={0} style={styles.actionCardGroup}>
        <ActionRow
          icon={Lightbulb}
          iconTone="green"
          title="Need content ideas?"
          body="See what other athletes are posting that's actually getting sign-ups."
          onPress={() => void handleOpenUrl(CONTENT_IDEAS_URL)}
          accessibilityLabel="Need content ideas — open Archive"
          testID="howitworks-content-ideas"
        />
        <View style={styles.actionDivider} />
        <ActionRow
          icon={MessageCircle}
          iconTone="blue"
          title="Want to know what's working?"
          body="Join the Discord with other athletes in the deal."
          onPress={() => void handleOpenUrl(DISCORD_URL)}
          accessibilityLabel="Want to know what's working — open Discord"
          testID="howitworks-discord"
        />
      </Card>

      <FaqCard
        title="Stuff your friends will ask"
        subtitle="Answers you can copy/paste if they push back."
        items={faqs}
      />

      {terms ? (
        <Card variant="flat" padding={0} style={styles.card}>
          <CardTitle>Terms</CardTitle>
          <Text variant="body" color="text" style={styles.termsBody}>
            {terms}
          </Text>
        </Card>
      ) : null}

      <ActionRow
        icon={Mail}
        iconTone="frost"
        title="Still have questions?"
        body="Email jason@marketpryce.com"
        onPress={onContactSupport}
        accessibilityLabel="Still have questions — email Jason at MarketPryce"
        testID="howitworks-help"
        wrapInCard
      />
    </View>
  );
}

/**
 * Card section heading. Matches the action-row title font (Inter Bold)
 * but bumped a step up in size so the section reads as a label, not as
 * a peer of the row titles inside it.
 */
function CardTitle({ children }: { children: string }) {
  return (
    <Text variant="bodyBold" color="deepGreen" style={styles.cardTitle}>
      {children}
    </Text>
  );
}

interface RewardRowProps {
  icon: LucideIcon;
  title: string;
  body: string;
}

function RewardRow({ icon: Icon, title, body }: RewardRowProps) {
  return (
    <View style={styles.rewardRow}>
      <View style={styles.rewardIcon}>
        <Icon size={18} color={colors.electricGreen} strokeWidth={2.25} />
      </View>
      <View style={styles.rewardCopy}>
        <Text variant="bodyBold" color="deepGreen" style={styles.rewardTitle}>
          {title}
        </Text>
        <Text variant="body" color="textMuted" style={styles.itemBody}>
          {body}
        </Text>
      </View>
    </View>
  );
}

interface StepRowProps {
  number: number;
  body: string;
}

function StepRow({ number, body }: StepRowProps) {
  return (
    <View style={styles.stepRow}>
      <StepNumber number={number} />
      <Text variant="body" color="text" style={styles.itemBody}>
        {body}
      </Text>
    </View>
  );
}

interface ActionRowProps {
  icon: LucideIcon;
  iconTone: 'green' | 'blue' | 'frost';
  title: string;
  body: string;
  onPress: () => void;
  accessibilityLabel: string;
  testID?: string;
  /** When true, renders its own bordered Card so the row stands alone. */
  wrapInCard?: boolean;
}

function ActionRow({
  icon: Icon,
  iconTone,
  title,
  body,
  onPress,
  accessibilityLabel,
  testID,
  wrapInCard,
}: ActionRowProps) {
  const content = (
    <Pressable
      onPress={onPress}
      accessibilityRole="link"
      accessibilityLabel={accessibilityLabel}
      style={({ pressed }) => [styles.actionRow, pressed && styles.actionRowPressed]}
      testID={testID}
    >
      <View
        style={[
          styles.actionIcon,
          iconTone === 'green' && styles.actionIconGreen,
          iconTone === 'blue' && styles.actionIconBlue,
          iconTone === 'frost' && styles.actionIconFrost,
        ]}
      >
        <Icon size={18} color={colors.deepGreen} strokeWidth={2.25} />
      </View>
      <View style={styles.actionCopy}>
        <Text variant="bodyBold" color="deepGreen">
          {title}
        </Text>
        <Text variant="caption" color="textMuted" style={styles.actionBody}>
          {body}
        </Text>
      </View>
      <ArrowUpRight size={18} color={colors.textMuted} strokeWidth={2} style={styles.actionChevron} />
    </Pressable>
  );

  if (wrapInCard) {
    return (
      <Card variant="flat" padding={0} style={styles.standaloneActionCard}>
        {content}
      </Card>
    );
  }
  return content;
}

const styles = StyleSheet.create({
  container: {
    gap: spacing.md,
  },
  card: {
    padding: spacing.lg,
    gap: spacing.md,
  },
  cardTitle: {
    // Inter Bold (via `variant="bodyBold"`) at a slightly larger size
    // so section headers read as labels, not as peers of the row
    // titles below them. Per user feedback 2026-05-11: section titles
    // should match the "Need content ideas?" font, just a touch larger.
    fontSize: fontSizes.lg,
    lineHeight: 24,
  },
  itemList: {
    gap: spacing.md,
  },
  itemBody: {
    flex: 1,
    lineHeight: 22,
  },
  rewardRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  rewardIcon: {
    width: 32,
    height: 32,
    borderRadius: radii.full,
    backgroundColor: colors.accentTint,
    borderWidth: 1.5,
    borderColor: colors.electricGreen,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  rewardCopy: {
    flex: 1,
    gap: 4,
  },
  rewardTitle: {
    fontSize: fontSizes.base,
    lineHeight: 22,
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  // Top-align (not center) so the icon sits next to the title row,
  // not floating in the middle of multi-line wrapping body text.
  actionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    padding: spacing.lg,
  },
  actionRowPressed: {
    opacity: 0.7,
  },
  actionCardGroup: {
    // No outer padding — each ActionRow owns its own `spacing.lg`
    // padding so the icon doesn't get pushed by a 48-of-padding
    // double-up from Card-padding + row-padding. Per user feedback
    // 2026-05-11.
    padding: 0,
  },
  actionDivider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.border,
    marginHorizontal: spacing.lg,
  },
  standaloneActionCard: {
    padding: 0,
  },
  actionIcon: {
    width: 36,
    height: 36,
    borderRadius: radii.base,
    alignItems: 'center',
    justifyContent: 'center',
    // Nudge to baseline-align with the title (bodyBold cap height).
    marginTop: 1,
  },
  actionIconGreen: {
    backgroundColor: colors.accentTint,
  },
  actionIconBlue: {
    backgroundColor: 'rgba(108, 198, 222, 0.18)',
  },
  actionIconFrost: {
    backgroundColor: colors.frost,
    borderWidth: 1,
    borderColor: colors.border,
  },
  actionCopy: {
    flex: 1,
    gap: 2,
  },
  actionBody: {
    lineHeight: 18,
  },
  actionChevron: {
    marginTop: 8,
  },
  termsBody: {
    lineHeight: 22,
  },
});
